/**
 * Shared field validators used across every BillBook form.
 *
 * Every validator returns `null` on success or a short, user-facing
 * error string on failure. Pass them straight into form-state plumbing
 * (`useForm`, custom resolvers, CrudForm validate hook).
 *
 * Indian regulatory formats covered:
 * - phone        — 10-digit mobile, optionally prefixed with +91 / 91 / 0
 * - email        — RFC-5322-lite (good enough for forms)
 * - pan          — 5 letters + 4 digits + 1 letter (e.g. AAAPL1234C)
 * - aadhaar      — 12 digits with Verhoeff checksum
 * - gstin        — 15 chars: 2-digit state + PAN + 1 entity + Z + 1 checksum
 * - ifsc         — 4 letters + 0 + 6 alphanumeric (e.g. SBIN0123456)
 * - pincode      — 6 digits, first digit not 0
 * - tan          — 4 letters + 5 digits + 1 letter
 * - cin          — 21-char Companies Act identifier
 * - upi          — VPA `<id>@<bank>`
 * - hsn          — 4–8 digit HSN/SAC code
 */

export type Validator = (value: unknown) => string | null;

/* -------------------------------------------------------------------- */
/*  Generic helpers                                                     */
/* -------------------------------------------------------------------- */

export function required(label = 'This field'): Validator {
  // Named so `chain()` can detect it and skip the empty-string short-circuit.
  return function requiredImpl(v) {
    if (v == null) return `${label} is required`;
    if (typeof v === 'string' && v.trim() === '') return `${label} is required`;
    if (Array.isArray(v) && v.length === 0) return `${label} is required`;
    return null;
  };
}

export function minLength(n: number, label = 'Value'): Validator {
  return (v) =>
    typeof v === 'string' && v.length >= n ? null : `${label} must be at least ${n} characters`;
}

export function maxLength(n: number, label = 'Value'): Validator {
  return (v) =>
    typeof v === 'string' && v.length <= n ? null : `${label} must be at most ${n} characters`;
}

export function pattern(re: RegExp, message: string): Validator {
  return (v) => (typeof v === 'string' && re.test(v) ? null : message);
}

/** Combine validators — first failure wins. Empty string skipped (treat as
 *  optional unless `required` is in the chain). */
export function chain(...vs: Validator[]): Validator {
  return (v) => {
    if (v == null || v === '') {
      const reqIdx = vs.findIndex((f) => f.name === 'requiredImpl');
      if (reqIdx === -1) return null;
    }
    for (const f of vs) {
      const e = f(v);
      if (e) return e;
    }
    return null;
  };
}

/* -------------------------------------------------------------------- */
/*  Indian regulatory                                                   */
/* -------------------------------------------------------------------- */

const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PINCODE_RE = /^[1-9][0-9]{5}$/;
const TAN_RE = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
const CIN_RE = /^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
const UPI_RE = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z][a-zA-Z]+$/;
const HSN_RE = /^[0-9]{4,8}$/;

/** RFC-5322-lite — fine for HTML forms. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** 10-digit Indian mobile starting with 6/7/8/9, with optional +91/91/0 prefix. */
const PHONE_RE = /^(?:\+?91|0)?[6-9]\d{9}$/;

/* -------------------------------------------------------------------- */
/*  Validators                                                          */
/* -------------------------------------------------------------------- */

export const isEmail: Validator = (v) =>
  typeof v === 'string' && EMAIL_RE.test(v.trim()) ? null : 'Enter a valid email';

export const isPhone: Validator = (v) =>
  typeof v === 'string' && PHONE_RE.test(v.replace(/\s+/g, ''))
    ? null
    : 'Enter a valid 10-digit mobile number';

export const isPan: Validator = (v) =>
  typeof v === 'string' && PAN_RE.test(v.toUpperCase().trim())
    ? null
    : 'PAN must be 5 letters + 4 digits + 1 letter (e.g. AAAPL1234C)';

export const isGstin: Validator = (v) =>
  typeof v === 'string' && GSTIN_RE.test(v.toUpperCase().trim())
    ? null
    : 'GSTIN must be 15 characters (e.g. 27AAAPL1234C1Z5)';

export const isIfsc: Validator = (v) =>
  typeof v === 'string' && IFSC_RE.test(v.toUpperCase().trim())
    ? null
    : 'IFSC must be 4 letters + 0 + 6 alphanumeric (e.g. SBIN0123456)';

export const isPincode: Validator = (v) =>
  typeof v === 'string' && PINCODE_RE.test(v.trim()) ? null : 'Enter a valid 6-digit PIN code';

export const isTan: Validator = (v) =>
  typeof v === 'string' && TAN_RE.test(v.toUpperCase().trim())
    ? null
    : 'TAN must be 4 letters + 5 digits + 1 letter';

export const isCin: Validator = (v) =>
  typeof v === 'string' && CIN_RE.test(v.toUpperCase().trim())
    ? null
    : 'Enter a valid 21-character CIN';

export const isUpi: Validator = (v) =>
  typeof v === 'string' && UPI_RE.test(v.trim()) ? null : 'Enter a valid UPI VPA (e.g. name@bank)';

export const isHsn: Validator = (v) =>
  typeof v === 'string' && HSN_RE.test(v.trim()) ? null : 'HSN/SAC must be 4–8 digits';

/**
 * Aadhaar — 12 digits with the Verhoeff checksum used by UIDAI. Plain
 * regex isn't enough; UIDAI ships specific checksums to weed out typos.
 *
 * Returns the trimmed-and-stripped value if valid, otherwise null. Use
 * via `isAadhaar` which wraps this.
 */
const VERHOEFF_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
  [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
  [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
  [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
  [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
  [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const VERHOEFF_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
  [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
  [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
  [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
  [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
  [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

function verhoeffOk(digits: string): boolean {
  let c = 0;
  const reversed = digits.split('').reverse();
  for (let i = 0; i < reversed.length; i++) {
    c = VERHOEFF_D[c]![VERHOEFF_P[i % 8]![Number(reversed[i])]!]!;
  }
  return c === 0;
}

export const isAadhaar: Validator = (v) => {
  if (typeof v !== 'string') return 'Aadhaar must be 12 digits';
  const stripped = v.replace(/\s|-/g, '');
  if (!/^\d{12}$/.test(stripped)) return 'Aadhaar must be 12 digits';
  if (!verhoeffOk(stripped)) return 'Aadhaar checksum is invalid';
  return null;
};

/* -------------------------------------------------------------------- */
/*  Numeric / date helpers                                              */
/* -------------------------------------------------------------------- */

export function min(n: number, label = 'Value'): Validator {
  return (v) => {
    const x = Number(v);
    return Number.isFinite(x) && x >= n ? null : `${label} must be at least ${n}`;
  };
}

export function max(n: number, label = 'Value'): Validator {
  return (v) => {
    const x = Number(v);
    return Number.isFinite(x) && x <= n ? null : `${label} must be at most ${n}`;
  };
}

export const isNonNegative: Validator = (v) =>
  Number.isFinite(Number(v)) && Number(v) >= 0 ? null : 'Must be 0 or greater';

export const isPositive: Validator = (v) =>
  Number.isFinite(Number(v)) && Number(v) > 0 ? null : 'Must be greater than 0';

/* -------------------------------------------------------------------- */
/*  Object-level validate(form) -> Record<field, error>                 */
/* -------------------------------------------------------------------- */

export type FormSchema<T> = { [K in keyof T]?: Validator };

/**
 * Run a per-field schema over an object and return either null (everything
 * passed) or a `{ field: errorMessage }` map. Designed to plug into
 * CrudForm's `validate` hook so every form gets the same Indian-regulatory
 * coverage with one line.
 *
 *   validate: validateSchema({
 *     name: chain(required('Name'), maxLength(100, 'Name')),
 *     email: isEmail,
 *     phone: isPhone,
 *     gstin: isGstin,
 *     pan: isPan,
 *   })
 */
export function validateSchema<T extends Record<string, unknown>>(
  schema: FormSchema<T>,
): (form: T) => string | null {
  return (form) => {
    const errors: string[] = [];
    for (const [key, fn] of Object.entries(schema) as [keyof T, Validator | undefined][]) {
      if (!fn) continue;
      const e = fn(form[key]);
      if (e) errors.push(`${String(key)}: ${e}`);
    }
    return errors.length === 0 ? null : errors.join('; ');
  };
}
